// src/components/adjustments/CategoryAdjustmentPanel.jsx
//
// Renders the record picker + embedded edit workflow for adjustment categories.
// Reuses the existing list APIs and existing Edit* components -- no new
// tables, no new routes, no duplicated save/validation logic.
import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowLeft, faSpinner, faInbox } from '@fortawesome/free-solid-svg-icons';
import api from '../../api';
import usePermissions from '../../hooks/usePermissions';
import { usePopup } from '../context/PopupContext';
import { useConfirm } from '../common/ui';
import EditPurchase from '../purchase/EditPurchase';
import EditIssuance from '../issuance/EditIssuance';
import EditWastage from '../wastage/EditWastage';
import PurchasingPaymentAdjustmentEditor from './PurchasingPaymentAdjustmentEditor';
import PettyCashVoucherAdjustmentEditor from './PettyCashVoucherAdjustmentEditor';
import ExpenseBillAdjustmentEditor from './ExpenseBillAdjustmentEditor';
import FinancialReceiptAdjustmentEditor from './FinancialReceiptAdjustmentEditor';
import IssuedAdjustmentEditor from './IssuedAdjustmentEditor';
import './Adjustments.css';

// Category -> { endpoint, columns, editor }
const CATEGORY_CONFIG = {
  Invoice: {
    endpoint: '/purchases',
    label: 'Invoice',
    dateKey: 'purchase_date',
    getRows: (data) => data,
    columns: [
      { key: 'invoice_no', label: 'Invoice No.' },
      { key: 'supplier_name', label: 'Supplier' },
      { key: 'purchase_date', label: 'Date', format: (v) => new Date(v).toLocaleDateString() },
      { key: 'payment_status', label: 'Status' },
    ],
    matches: (row, term) =>
      row.invoice_no?.toLowerCase().includes(term) ||
      row.supplier_name?.toLowerCase().includes(term),
    Editor: EditPurchase,
  },
  Issuance: {
    endpoint: '/issuances',
    label: 'Issuance',
    dateKey: 'issuance_date',
    getRows: (data) => data,
    columns: [
      { key: 'id', label: 'ID' },
      { key: 'department_name', label: 'Department' },
      { key: 'issuance_date', label: 'Date', format: (v) => new Date(v).toLocaleString() },
      { key: 'received_by', label: 'Received By' },
    ],
    matches: (row, term) =>
      row.department_name?.toLowerCase().includes(term) ||
      row.received_by?.toLowerCase().includes(term) ||
      String(row.id).includes(term),
    Editor: EditIssuance,
  },
  Wastage: {
    endpoint: '/wastages',
    label: 'Wastage',
    dateKey: 'date',
    getRows: (data) => data,
    columns: [
      { key: 'wastage_note_number', label: 'Note #' },
      { key: 'reason', label: 'Reason' },
      { key: 'date', label: 'Date', format: (v) => new Date(v).toLocaleDateString() },
      { key: 'approved_by', label: 'Approved By' },
    ],
    matches: (row, term) =>
      row.wastage_note_number?.toLowerCase().includes(term) ||
      row.approved_by?.toLowerCase().includes(term) ||
      row.reason?.toLowerCase().includes(term),
    Editor: EditWastage,
  },
  // This key must exactly match the value emitted by StoresBalance. Keeping
  // the selected composite payment/invoice ID intact is what makes an edit
  // apply to the intended payment allocation rather than another invoice.
  'Purchasing Payment': {
    endpoint: '/purchases/payments/adjustable',
    label: 'Purchasing Payment',
    dateKey: 'payment_date',
    getRows: (data) => data,
    columns: [
      { key: 'invoice_no', label: 'Invoice No.' },
      { key: 'supplier_name', label: 'Supplier' },
      { key: 'payment_date', label: 'Payment Date', format: (v) => new Date(v).toLocaleDateString() },
      { key: 'amount', label: 'Amount', format: (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}` },
      { key: 'voucher_number', label: 'Voucher No.' },
    ],
    matches: (row, term) =>
      row.invoice_no?.toLowerCase().includes(term) ||
      row.supplier_name?.toLowerCase().includes(term) ||
      row.voucher_number?.toLowerCase().includes(term),
    Editor: PurchasingPaymentAdjustmentEditor,
  },
  // ---- Petty Cash / Expenses ------------------------------------------
  // Editing moved here from the Petty Cash page. Every save is logged in the
  // Adjustment Summary (the editors send X-RM-Adjustment).
  'Petty Cash Voucher': {
    endpoint: '/expenses/petty-cash/vouchers?limit=1000',
    // Internal key/value stays 'Petty Cash Voucher' (matches the backend
    // category and rm_adjustment_records rows); only the displayed label is
    // "Petty Cash Adjustment".
    label: 'Petty Cash Adjustment',
    dateKey: 'date',
    getRows: (data) => data,
    columns: [
      { key: 'voucher_number', label: 'Voucher No.' },
      { key: 'date', label: 'Date', format: (v) => (v ? new Date(v).toLocaleDateString() : '-') },
      { key: 'payee', label: 'Payee' },
      { key: 'expense_type', label: 'Expense Type' },
      { key: 'sub_expense_type', label: 'Sub Expense Type' },
      { key: 'amount', label: 'Amount', format: (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}` },
    ],
    matches: (row, term) =>
      row.voucher_number?.toLowerCase().includes(term) ||
      row.payee?.toLowerCase().includes(term) ||
      row.expense_type?.toLowerCase().includes(term) ||
      row.sub_expense_type?.toLowerCase().includes(term),
    Editor: PettyCashVoucherAdjustmentEditor,
  },
  'Expense Bill': {
    endpoint: '/new-expenses',
    // Internal key/value stays 'Expense Bill' (matches the backend category
    // and rm_adjustment_records rows); only the displayed label is
    // "Expense Adjustment".
    label: 'Expense Adjustment',
    dateKey: 'bill_date',
    getRows: (data) => data,
    columns: [
      { key: 'bill_number', label: 'Bill No.' },
      { key: 'bill_date', label: 'Date', format: (v) => (v ? new Date(v).toLocaleDateString() : '-') },
      { key: 'payee_name', label: 'Payee' },
      { key: 'expense_type', label: 'Expense Type' },
      { key: 'sub_expense_type', label: 'Sub Expense Type' },
      { key: 'total_bill_amount', label: 'Total', format: (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}` },
      { key: 'payment_status', label: 'Status' },
    ],
    matches: (row, term) =>
      row.bill_number?.toLowerCase().includes(term) ||
      row.payee_name?.toLowerCase().includes(term) ||
      row.expense_type?.toLowerCase().includes(term) ||
      row.sub_expense_type?.toLowerCase().includes(term),
    // Editable for both Paid and Non-Paid bills - see server/routes/newExpenseRoutes.js
    // PUT /:id, which now allows edits regardless of paid_amount.
    Editor: ExpenseBillAdjustmentEditor,
  },
  'Cash Receive': {
    endpoint: '/adjustments/records/Cash%20Receive',
    label: 'Cash Receive',
    dateKey: 'date',
    getRows: (data) => data,
    columns: [
      { key: 'date', label: 'Date', format: (v) => v ? new Date(v).toLocaleDateString() : '-' },
      { key: 'reference_number', label: 'Reference No.' },
      { key: 'company_name', label: 'Company' },
      { key: 'receive_type', label: 'Receive Type' },
      { key: 'amount', label: 'Amount', format: (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}` },
      { key: 'remark', label: 'Description / Remark' },
      { key: 'created_by_name', label: 'Entered By' },
    ],
    matches: (row, term) =>
      row.reference_number?.toLowerCase().includes(term) ||
      row.company_name?.toLowerCase().includes(term) ||
      row.receive_type?.toLowerCase().includes(term) ||
      row.remark?.toLowerCase().includes(term),
    // Every entry, Pending or Completed (card) / posted (finance), can be
    // edited or voided - but only by the restricted adjustment accounts
    // (NEW_USER role / any Super Admin account).
    restrictedOnly: true,
    voidable: true,
    voidEndpoint: (id) => `/adjustments/records/Cash%20Receive/finance/${id}`,
    // High-volume card/POS rows (always "Visa Card Payment") can bury the far
    // rarer plain receive types (Opening Balance, Previous Collection, ...)
    // past the 50-row preview below - let the user narrow to one type first.
    hasReceiveTypeFilter: true,
    Editor: FinancialReceiptAdjustmentEditor,
  },
  'Bank Receive': {
    endpoint: '/adjustments/records/Bank%20Receive',
    label: 'Bank Receive',
    dateKey: 'date',
    getRows: (data) => data,
    columns: [
      { key: 'date', label: 'Date', format: (v) => v ? new Date(v).toLocaleDateString() : '-' },
      { key: 'reference_number', label: 'Reference No.' },
      { key: 'company_name', label: 'Company' },
      { key: 'bank_name', label: 'Bank Account', format: (v, row) => `${v || '-'}${row.account_number ? ` — ${row.account_number}` : ''}` },
      { key: 'receive_type', label: 'Receive Type' },
      { key: 'amount', label: 'Amount', format: (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}` },
      { key: 'remark', label: 'Description / Remark' },
      { key: 'status', label: 'Status', format: (v, row) => row.source === 'card' ? v : 'Posted' },
      { key: 'created_by_name', label: 'Entered By' },
    ],
    matches: (row, term) =>
      row.reference_number?.toLowerCase().includes(term) ||
      row.company_name?.toLowerCase().includes(term) ||
      row.bank_name?.toLowerCase().includes(term) ||
      row.receive_type?.toLowerCase().includes(term) ||
      row.remark?.toLowerCase().includes(term),
    // Every entry, Pending or Completed (card) / posted (finance), can be
    // edited or voided - but only by the restricted adjustment accounts
    // (NEW_USER role / any Super Admin account).
    restrictedOnly: true,
    voidable: true,
    voidEndpoint: (id) => `/adjustments/records/Bank%20Receive/finance/${id}`,
    // High-volume card/POS rows (always "Visa Card Payment") can bury the far
    // rarer plain receive types (Opening Balance, Previous Collection, ...)
    // past the 50-row preview below - let the user narrow to one type first.
    hasReceiveTypeFilter: true,
    Editor: FinancialReceiptAdjustmentEditor,
  },
  'Cash Issued': {
    endpoint: '/finance/cash/issued',
    label: 'Cash Issued',
    dateKey: 'date',
    getRows: (data) => data,
    columns: [
      { key: 'date', label: 'Date', format: (v) => v ? new Date(v).toLocaleDateString() : '-' },
      { key: 'reference_number', label: 'Reference No.' },
      { key: 'company_name', label: 'Company' },
      { key: 'other_ledger_name', label: 'Other Ledger' },
      { key: 'amount', label: 'Amount', format: (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}` },
      { key: 'remark', label: 'Description / Remark' },
      { key: 'created_by_name', label: 'Entered By' },
    ],
    matches: (row, term) =>
      row.reference_number?.toLowerCase().includes(term) ||
      row.company_name?.toLowerCase().includes(term) ||
      row.other_ledger_name?.toLowerCase().includes(term) ||
      row.remark?.toLowerCase().includes(term),
    // Edited or voided only by the restricted adjustment accounts (NEW_USER
    // role / any Super Admin account) - amount may be negative here.
    restrictedOnly: true,
    voidable: true,
    voidEndpoint: (id) => `/finance/cash/issued/${id}`,
    Editor: IssuedAdjustmentEditor,
  },
  'Bank Issued': {
    endpoint: '/finance/bank/issued',
    label: 'Bank Issued',
    dateKey: 'date',
    getRows: (data) => data,
    columns: [
      { key: 'date', label: 'Date', format: (v) => v ? new Date(v).toLocaleDateString() : '-' },
      { key: 'reference_number', label: 'Reference No.' },
      { key: 'company_name', label: 'Company' },
      { key: 'bank_name', label: 'Bank Account', format: (v, row) => `${v || '-'}${row.account_number ? ` — ${row.account_number}` : ''}` },
      { key: 'other_ledger_name', label: 'Other Ledger' },
      { key: 'amount', label: 'Amount', format: (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}` },
      { key: 'remark', label: 'Description / Remark' },
      { key: 'created_by_name', label: 'Entered By' },
    ],
    matches: (row, term) =>
      row.reference_number?.toLowerCase().includes(term) ||
      row.company_name?.toLowerCase().includes(term) ||
      row.bank_name?.toLowerCase().includes(term) ||
      row.other_ledger_name?.toLowerCase().includes(term) ||
      row.remark?.toLowerCase().includes(term),
    // Edited or voided only by the restricted adjustment accounts (NEW_USER
    // role / any Super Admin account) - amount may be negative here.
    restrictedOnly: true,
    voidable: true,
    voidEndpoint: (id) => `/finance/bank/issued/${id}`,
    Editor: IssuedAdjustmentEditor,
  },
};

const CategoryAdjustmentPanel = ({ category, onAdjustmentSaved }) => {
  const config = CATEGORY_CONFIG[category];
  const { canAdjustRestricted } = usePermissions();
  const { showPopup } = usePopup();
  const confirm = useConfirm();
  const [voidingId, setVoidingId] = useState(null);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [receiveType, setReceiveType] = useState('');

  const fetchRecords = useCallback(async () => {
    if (!config) return;
    try {
      setLoading(true);
      setError('');
      const response = await api.get(config.endpoint);
      setRecords(config.getRows(response.data) || []);
    } catch (err) {
      console.error(`Error fetching ${category} records:`, err);
      setError(`Unable to load ${config.label.toLowerCase()} records. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, [category, config]);

  useEffect(() => {
    setSelectedId(null);
    setSelectedSource(null);
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setReceiveType('');
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  if (!config) return null;

  // ---- Editing an existing record ----
  if (selectedId !== null) {
    const { Editor } = config;
    return (
      <div className="form-section">
        <button
          type="button"
          className="btn btn-secondary"
          style={{ marginBottom: '16px' }}
          onClick={() => { setSelectedId(null); setSelectedSource(null); }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to {config.label} List
        </button>
        <Editor
          embeddedId={selectedId}
          category={category}
          source={selectedSource}
          isRmAdjustment
          onCancel={() => { setSelectedId(null); setSelectedSource(null); }}
          onSaved={() => {
            setSelectedId(null);
            setSelectedSource(null);
            fetchRecords();
            if (onAdjustmentSaved) onAdjustmentSaved(category);
          }}
        />
      </div>
    );
  }

  // ---- Picking a record ----
  // Distinct receive types present, for the filter dropdown below - sorted
  // with "Visa Card Payment" last since every other type is comparatively rare.
  const receiveTypeOptions = config.hasReceiveTypeFilter
    ? [...new Set(records.map((row) => row.receive_type).filter(Boolean))].sort((a, b) =>
        (a === 'Visa Card Payment') - (b === 'Visa Card Payment') || a.localeCompare(b))
    : [];

  const term = search.trim().toLowerCase();
  let filtered = term
    ? records.filter((row) => config.matches(row, term))
    : records;

  if (config.hasReceiveTypeFilter && receiveType) {
    filtered = filtered.filter((row) => row.receive_type === receiveType);
  }

  // Date range filter, keyed off whichever date field this category uses.
  if (config.dateKey && (dateFrom || dateTo)) {
    filtered = filtered.filter((row) => {
      const raw = row[config.dateKey];
      if (!raw) return false;
      const rowDate = String(raw).slice(0, 10);
      if (dateFrom && rowDate < dateFrom) return false;
      if (dateTo && rowDate > dateTo) return false;
      return true;
    });
  }

  return (
    <div className="form-section">
      <h3 className="form-section-title">
        <FontAwesomeIcon icon={faSearch} /> Select {config.label} Record to Adjust
      </h3>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end', marginBottom: '16px' }}>
        <div className="form-group" style={{ maxWidth: '420px', marginBottom: 0 }}>
          <input
            type="text"
            placeholder={`Search ${config.label.toLowerCase()} records...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {config.hasReceiveTypeFilter && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Receive Type</label>
            <select value={receiveType} onChange={(e) => setReceiveType(e.target.value)}>
              <option value="">All types</option>
              {receiveTypeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>
        )}
        {config.dateKey && (
          <>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
              >
                Clear Dates
              </button>
            )}
          </>
        )}
      </div>

      {loading ? (
        <p><FontAwesomeIcon icon={faSpinner} spin /> Loading {config.label.toLowerCase()} records...</p>
      ) : error ? (
        <div className="alert-message error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faInbox} style={{ fontSize: '28px', opacity: 0.3 }} />
          <p>No {config.label.toLowerCase()} records found.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="adjustments-table">
            <thead>
              <tr>
                {config.columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th style={{ width: '170px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 50).map((row) => (
                <tr key={`${row.source || category}-${row.id}`}>
                  {config.columns.map((col) => (
                    <td key={col.key}>
                      {col.format ? col.format(row[col.key], row) : (row[col.key] ?? '-')}
                    </td>
                  ))}
                  <td style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => { setSelectedId(String(row.id)); setSelectedSource(row.source || 'finance'); }}
                      disabled={config.restrictedOnly && !canAdjustRestricted}
                      title={config.restrictedOnly && !canAdjustRestricted ? 'Only the restricted adjustment accounts can edit this record' : undefined}
                    >
                      Edit
                    </button>
                    {config.voidable && canAdjustRestricted && (row.source || 'finance') === 'finance' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        disabled={voidingId === row.id}
                        onClick={async () => {
                          if (!await confirm(`Void this ${config.label} transaction? Its Other Ledger entry will be reversed as well.`)) return;
                          setVoidingId(row.id);
                          try {
                            await api.delete(config.voidEndpoint(row.id));
                            showPopup('success', `${config.label} voided successfully`);
                            fetchRecords();
                            if (onAdjustmentSaved) onAdjustmentSaved(category);
                          } catch (err) {
                            showPopup('error', err.response?.data?.error || `Failed to void ${config.label.toLowerCase()}`);
                          } finally {
                            setVoidingId(null);
                          }
                        }}
                      >
                        Void
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 50 && (
            <p className="help-text">Showing first 50 of {filtered.length} results. Refine your search to narrow down.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryAdjustmentPanel;